import { redirect } from 'react-router-dom';

// Constants
import { formNaming } from '../../../../../../components/forms/NewCommunicationForm/constants';
import { tabsType } from '../../../../../../state/reducers/ui/tabs';

// GQL
import { client } from '../../../../../../gql/client';
import { SAVE_COMMUNICATION } from '../../../../../../gql/mutations/communication';
import { SAVE_GENERIC_ORDER } from '../../../../../../gql/mutations/generic-order';
import { toTeamIds } from '../../../../../../utils/forms';
import { SAVE_CAMPAIGN } from '../../../../../../gql/mutations/campaign';
import { GET_ORDER_BY_ID } from '../../../../../../gql/queries/orders/orders';
import {
  prependCampaignToCache,
  prependCommunicationToCache,
  prependGenericOrderToCache,
} from '../../../../../../gql/cacheHelpers';
import { GET_COMMUNICATION_BY_ID } from '../../../../../../gql/queries/communication';

// Redux
import { indexTabFormReset } from '../../../../../../state/actions/ui/tabs';
import { deselectedRowModalTable } from '../../../../../../state/actions/ui/selectRowTableModal';
import { setCursor } from '../../../../../../state/actions/ui/cursorTables';
import { getCursor } from '../../../../../../state/selectors/ui/tables';

// Types
import { OrderState, OrderType } from '../../../../../../types/orders';
import {
  CommunicationInput,
  SaveDataParams,
  orderCommunicationInputTypes,
} from './types';
import {
  tableIds,
  takeBase,
} from '../../../../../../components/display/tables/types';

// Utils
import { decompressSessionStorage } from '../../../../../../utils/sessionStorage';
import {
  assertDraftOwner,
  DraftOwnerMismatchError,
} from '../../../../../../utils/draftOwnership';
import { COMMUNICATION_DUPLICATE_SCOPE } from '../../../../../../utils/draftScopes';
import { readDraftCopyId, requireDraftOwner } from '../../../../../../utils/ownedDraftCopy';

import { getCommunicationInput } from './utils';

export const saveData = async ({
  id,
  tabSelected,
  storedDataOTSelected,
  store,
}: SaveDataParams) => {
  let orderCommunicationInput: orderCommunicationInputTypes = {
      type: OrderType.Communication,
      state: OrderState.Open,
      creationDate: '',
      registerInitDate: '',
      specialTag: '',
      description: '',
      notes: '',
      classification: '',
      capitol: '',
      subCapitol: '',
      parentExpeditionOrderID: '',
      currentExpeditionIndex: '',
    },
    communicationInput: CommunicationInput = {};
  const dispatch = store.dispatch;
  let dataForm = null;
  // Tarea #8 (2026-04-24): tab 0 en duplicate ahora crea nueva OT como en new-communication.
  let dataOrderParent: any = null;

  const cursorDuplicateCommunication = getCursor(
    tableIds.DUPLICATE_COMMUNICATION
  )(store.getState());
  const cursorSearchAndCreateCommunication = getCursor(
    tableIds.SEARCH_AND_CREATE_HISTORICAL_COMMUNICATIONS
  )(store.getState());
  const cursorWorkOrdersCommunication = getCursor(
    tableIds.WORKORDER_COMMUNICATIONS
  )(store.getState());
  const cursorCampaign = getCursor(tableIds.CAMPAIGNS)(store.getState());
  const cursorCampaignModal = getCursor(tableIds.CAMPAIGN_MODAL)(
    store.getState()
  );
  const cursorGenericOrder = getCursor(tableIds.WORKORDER_GENERAL_VIEW)(
    store.getState()
  );
  const cursorGenericOrderDuplicate = getCursor(
    tableIds.DUPLICATE_GENERIC_ORDER
  )(store.getState());
  const cursorGenericOrderSearchAndCreate = getCursor(
    tableIds.SEARCH_AND_CREATE_HISTORICAL_WORKORDERS
  )(store.getState());
  const cursorGenericOrderModal = getCursor(tableIds.GENERIC_ORDER_MODAL)(
    store.getState()
  );

  // Barrera: guardar con la COPIA del formulario exige que sea del origen que
  // reclamó la URL. El guardado directo «Ver» → «Guardar duplicado» no tiene
  // copia (su puerta la borra) y no pasa por aquí. Falla CERRADA: una copia sin
  // `id` de origen, o con otro, o sin dueño, no escribe ni encola nada. La cola
  // offline reproduce la mutación ya capturada y no pasa por esta barrera.
  if (sessionStorage.getItem(formNaming.NEW_DUPLICATE_COMMUNICATION_FORM)) {
    const sourceId = id?.toString();
    assertDraftOwner(COMMUNICATION_DUPLICATE_SCOPE, sourceId, { isReplay: false });
    requireDraftOwner(COMMUNICATION_DUPLICATE_SCOPE, sourceId);
    if (!sourceId) {
      throw new DraftOwnerMismatchError(COMMUNICATION_DUPLICATE_SCOPE.name, null, '(sin id)');
    }
    // La copia, si dice de quién es, tiene que ser del origen que se guarda.
    const copyId = readDraftCopyId(formNaming.NEW_DUPLICATE_COMMUNICATION_FORM);
    if (copyId && copyId !== sourceId) {
      throw new DraftOwnerMismatchError(
        COMMUNICATION_DUPLICATE_SCOPE.name,
        copyId,
        sourceId
      );
    }
  }

  const creationDate = new Date().toISOString();
  /// Step 1: Get data Communication
  const {
    data: { order },
  } = await client.query({
    query: GET_ORDER_BY_ID,
    variables: {
      id: id,
    },
  });
  const dataFormRaw = sessionStorage.getItem(
    formNaming.NEW_DUPLICATE_COMMUNICATION_FORM
  );
  if (dataFormRaw) {
    dataForm = JSON.parse(decompressSessionStorage(dataFormRaw));
  }

  // Tarea #8 (2026-04-24): tab 0 = "Crear nueva OT" (antes reutilizaba el padre
  // de la comunicación original). Ahora crea parent Order + GenericOrder/Campaign,
  // igual que NewComunication/saveData.ts, y propaga teamRelationships.
  if (tabSelected === 0 && dataForm) {
    const pkInitArrayParent = dataForm.pkInit.replace(/ /g, '').split('+');
    const pkEndArrayParent = dataForm.pkEnd.replace(/ /g, '').split('+');
    const isCampaignParent = dataForm?.specialTag === OrderType.Campaign;

    // Bloque C-6: SAVE_CAMPAIGN o SAVE_GENERIC_ORDER atómicos sustituyen
    // CREATE_NEW_ORDER + CREATE_NEW_CAMPAIGN/GENERIC_ORDER por una transacción
    // Prisma única — backend genera ROID, Order ↔ Entity coherentes desde el
    // primer momento (no se necesita UPDATE_ORDER post-create).
    if (isCampaignParent) {
      dataOrderParent = await client.mutate({
        mutation: SAVE_CAMPAIGN,
        // El padre de una comunicación no se encola suelto: si se encolara solo
        // él, la SAVE_COMMUNICATION hija se perdería. Sin red del todo ya se entró
        // por la rama compound; aquí, si falla, es error (con toast).
        context: { skipOfflineQueue: true },
        variables: {
          input: {
            // Idempotencia online: UUID estable por operación (RetryLink no duplica).
            clientOpId: crypto.randomUUID(),
            state: OrderState.Open,
            creationDate,
            registerInitDate: dataForm.registerInitDate,
            // Autor elegido en el formulario; el backend lo valida y, si no
            // vale, pone al autenticado (nunca rechaza).
            ...(dataForm.userId && { userId: dataForm.userId }),
            pkInitKm: Number(pkInitArrayParent[0]),
            pkInitMeter: Number(pkInitArrayParent[1]),
            pkEndKm: Number(pkEndArrayParent[0]),
            pkEndMeter: Number(pkEndArrayParent[1]),
            track: dataForm.track,
            direction: dataForm?.direction,
            margin: dataForm?.margin,
            lane: dataForm.lane,
            concessionId: dataForm.concession,
            roadId: dataForm.road,
            teamIds: toTeamIds(dataForm.teamRelationships),
            capitolId: dataForm.capitol,
            subCapitolId: dataForm.subCapitol,
            classification: dataForm.classification,
            ...(dataForm.predefine && {
              predefinedOrderId: dataForm.predefine,
            }),
            description: dataForm.description,
            notes: dataForm.notes,
            name: `Campanya relacionada amb una comunicacio`,
          },
        },
        update(cache, { data: { saveCampaign } }) {
          prependCampaignToCache(cache, saveCampaign.campaign);
          if (cursorCampaign?.cursor !== undefined) {
            dispatch(setCursor(tableIds.CAMPAIGNS, {
              ...cursorCampaign, take: takeBase, cursor: '', orderBy: 'desc',
            }));
            dispatch(setCursor(tableIds.CAMPAIGN_MODAL, {
              ...cursorCampaignModal, take: takeBase, cursor: '', orderBy: 'desc',
            }));
          }
        },
      });
    } else {
      dataOrderParent = await client.mutate({
        mutation: SAVE_GENERIC_ORDER,
        // El padre de una comunicación no se encola suelto: si se encolara solo
        // él, la SAVE_COMMUNICATION hija se perdería. Sin red del todo ya se entró
        // por la rama compound; aquí, si falla, es error (con toast).
        context: { skipOfflineQueue: true },
        variables: {
          input: {
            // Idempotencia online: UUID estable por operación (RetryLink no duplica).
            clientOpId: crypto.randomUUID(),
            state: OrderState.Open,
            creationDate,
            registerInitDate: dataForm.registerInitDate,
            // Autor elegido en el formulario; el backend lo valida y, si no
            // vale, pone al autenticado (nunca rechaza).
            ...(dataForm.userId && { userId: dataForm.userId }),
            pkInitKm: Number(pkInitArrayParent[0]),
            pkInitMeter: Number(pkInitArrayParent[1]),
            pkEndKm: Number(pkEndArrayParent[0]),
            pkEndMeter: Number(pkEndArrayParent[1]),
            track: dataForm.track,
            direction: dataForm?.direction,
            margin: dataForm?.margin,
            lane: dataForm.lane,
            concessionId: dataForm.concession,
            roadId: dataForm.road,
            teamIds: toTeamIds(dataForm.teamRelationships),
            capitolId: dataForm.capitol,
            subCapitolId: dataForm.subCapitol,
            classification: dataForm.classification,
            ...(dataForm.predefine && {
              predefinedOrderId: dataForm.predefine,
            }),
            description: dataForm.description,
            notes: dataForm.notes,
          },
        },
        update(cache, { data: { saveGenericOrder } }) {
          prependGenericOrderToCache(cache, saveGenericOrder.genericOrder);
          if (cursorGenericOrder?.cursor !== undefined) {
            dispatch(setCursor(tableIds.WORKORDER_GENERAL_VIEW, {
              ...cursorGenericOrder, take: takeBase, cursor: '', orderBy: 'desc',
            }));
            dispatch(setCursor(tableIds.DUPLICATE_GENERIC_ORDER, {
              ...cursorGenericOrderDuplicate, take: takeBase, cursor: '', orderBy: 'desc',
            }));
            dispatch(setCursor(tableIds.SEARCH_AND_CREATE_HISTORICAL_WORKORDERS, {
              ...cursorGenericOrderSearchAndCreate, take: takeBase, cursor: '', orderBy: 'desc',
            }));
            dispatch(setCursor(tableIds.GENERIC_ORDER_MODAL, {
              ...cursorGenericOrderModal, take: takeBase, cursor: '', orderBy: 'desc',
            }));
          }
        },
      });
    }

    // Bloque C-6: parent ahora viene de SAVE_CAMPAIGN o SAVE_GENERIC_ORDER.
    const newParentOrder =
      dataOrderParent?.data?.saveCampaign?.order ??
      dataOrderParent?.data?.saveGenericOrder?.order;

    // Refetch parent order
    if (newParentOrder?.id) {
      client.query({
        fetchPolicy: 'network-only',
        query: GET_ORDER_BY_ID,
        variables: { id: newParentOrder.id },
      });
    }

    // Step 3: Build Communication Order input pointing at the new parent
    orderCommunicationInput = {
      type: OrderType.Communication,
      state: OrderState.Open,
      creationDate,
      registerInitDate: dataForm.registerInitDate,
      specialTag: null,
      description: dataForm.description,
      notes: dataForm.notes,
      classification: dataForm.classification,
      capitol: dataForm.capitol,
      subCapitol: dataForm.subCapitol,
      ...(dataForm.predefine && { predefinedOrder: dataForm.predefine }),
      parentExpeditionOrderID: newParentOrder?.id,
      currentExpeditionIndex: newParentOrder?.indexExpedition,
    };
  } else if (tabSelected === 0) {
    // Fallback: si no hay dataForm (usuario no tocó el form), mantener comportamiento
    // legacy de colgar la comunicación del mismo padre que la original.
    // La fecha NO es la de la original: el duplicado es un registro nuevo de
    // quien duplica, con la hora del momento en que duplica (spec § 8).
    orderCommunicationInput = {
      type: OrderType.Communication,
      state: OrderState.Open,
      creationDate,
      registerInitDate: creationDate,
      specialTag: null,
      description: order.description,
      notes: order.notes,
      classification: order.classificationIDs[0],
      capitol: order.capitolIDs[0],
      subCapitol: order.subCapitolIDs[0],
      parentExpeditionOrderID: order.parentExpeditionOrderIDs[0],
      currentExpeditionIndex: order.indexExpedition,
    };
  }

  if (tabSelected > 0) {
    if (!storedDataOTSelected?.id) {
      throw new Error('No OT/Campaign selected for duplicate communication');
    }

    const {
      data: { order },
    } = await client.query({
      query: GET_ORDER_BY_ID,
      variables: {
        id: storedDataOTSelected?.id,
      },
    });

    orderCommunicationInput = {
      type: OrderType.Communication,
      state: OrderState.Open,
      creationDate,
      // Sin formulario caía a la fecha de la OT PADRE elegida: ahora, la de
      // duplicar.
      registerInitDate: dataForm?.registerInitDate ?? creationDate,
      description: dataForm?.description ?? order.description,
      notes: dataForm?.notes ?? order.notes,
      // Use communication's own values from form, fallback to parent order
      classification: dataForm?.classification ?? order?.classificationIDs?.[0],
      capitol: dataForm?.capitol ?? order.capitolIDs[0],
      subCapitol: dataForm?.subCapitol ?? order.subCapitolIDs[0],
      ...(dataForm?.predefine && { predefinedOrder: dataForm.predefine }),
      parentExpeditionOrderID: order.id,
      currentExpeditionIndex: order.indexExpedition,
    };
  }

  if (!dataForm) {
    const {
      data: { communication },
    } = await client.query({
      query: GET_COMMUNICATION_BY_ID,
      variables: {
        id: order.communicationIDs[0],
      },
    });

    communicationInput = getCommunicationInput(communication);
  } else {
    const pkInitArray = dataForm.pkInit.replace(/ /g, '').split('+');
    const pkEndArray = dataForm.pkEnd.replace(/ /g, '').split('+');

    communicationInput = {
      sendStatus: dataForm.sendStatus,
      creationDate,
      registerInitDate: dataForm?.registerInitDate ?? creationDate,
      interlocutorGroupId: dataForm.groupInter,
      mediaOfCommunicationId: dataForm.media,
      concessionId: dataForm.concession,
      roadId: dataForm.road,
      track: dataForm.track,
      lane: dataForm.lane,
      direction: dataForm?.direction,
      margin: dataForm?.margin,
      pkInitKm: Number(pkInitArray[0]),
      pkInitMeter: Number(pkInitArray[1]),
      pkEndKm: Number(pkEndArray[0]),
      pkEndMeter: Number(pkEndArray[1]),
    };
  }

  // Bloque C-1: SAVE_COMMUNICATION sustituye CREATE_NEW_ORDER + CREATE_NEW_COMMUNICATION
  // (+ UPDATE_ORDER + UPDATE_ORDER_RELATIONSHIPS post-create) por una mutación atómica.
  // El backend infiere indexExpedition/indexType del parentOrderId.
  const parentOrderIdForSave = orderCommunicationInput?.parentExpeditionOrderID;

  const dataSaveCommunication = await client.mutate({
    mutation: SAVE_COMMUNICATION,
    variables: {
      input: {
        // Idempotencia online: UUID estable por operación (RetryLink no duplica).
        clientOpId: crypto.randomUUID(),
        parentOrderId: parentOrderIdForSave,
        sendStatus: communicationInput.sendStatus,
        state: orderCommunicationInput?.state,
        creationDate: communicationInput.creationDate ?? creationDate,
        registerInitDate:
          communicationInput.registerInitDate ?? orderCommunicationInput?.registerInitDate,
        // Solo por el camino del formulario: el guardado directo no lo manda
        // y el backend pone al que duplica.
        ...(dataForm?.userId && { userId: dataForm.userId }),
        pkInitKm: communicationInput.pkInitKm,
        pkInitMeter: communicationInput.pkInitMeter,
        pkEndKm: communicationInput.pkEndKm,
        pkEndMeter: communicationInput.pkEndMeter,
        track: communicationInput.track,
        direction: communicationInput.direction,
        margin: communicationInput.margin,
        lane: communicationInput.lane,
        concessionId: communicationInput.concessionId,
        roadId: communicationInput.roadId,
        capitolId: dataForm?.capitol,
        subCapitolId: dataForm?.subCapitol,
        classification:
          dataForm?.classification ?? order?.classificationIDs?.[0],
        ...(dataForm?.predefine && { predefinedOrderId: dataForm.predefine }),
        description: orderCommunicationInput?.description,
        notes: orderCommunicationInput?.notes,
        interlocutorGroupId: communicationInput.interlocutorGroupId,
        mediaOfCommunicationId: communicationInput.mediaOfCommunicationId,
      },
    },
    update(cache, { data: { saveCommunication } }) {
      prependCommunicationToCache(
        cache,
        saveCommunication,
        parentOrderIdForSave || undefined,
      );
      dispatch(setCursor(tableIds.DUPLICATE_COMMUNICATION, {
        ...cursorDuplicateCommunication, take: takeBase, cursor: '', orderBy: 'desc',
      }));
      dispatch(setCursor(tableIds.WORKORDER_COMMUNICATIONS, {
        ...cursorWorkOrdersCommunication, take: takeBase, cursor: '', orderBy: 'desc',
      }));
      dispatch(setCursor(tableIds.SEARCH_AND_CREATE_HISTORICAL_COMMUNICATIONS, {
        ...cursorSearchAndCreateCommunication, take: takeBase, cursor: '', orderBy: 'desc',
      }));
    },
  });

  // Refetch del Order Communication child
  client.query({
    fetchPolicy: 'network-only',
    query: GET_ORDER_BY_ID,
    variables: { id: dataSaveCommunication.data.saveCommunication.order.id },
  });

  // Clean the form local storage as it was just saved
  // Clean up all possible keys (base, OT, and Campaign)
  sessionStorage.removeItem(formNaming.NEW_DUPLICATE_COMMUNICATION_FORM);
  sessionStorage.removeItem(COMMUNICATION_DUPLICATE_SCOPE.ownerKey);
  sessionStorage.removeItem(formNaming.NEW_DUPLICATE_COMMUNICATION_FORM_OT);
  sessionStorage.removeItem(formNaming.NEW_DUPLICATE_COMMUNICATION_FORM_CAMPAIGN);
  
  dispatch(
    deselectedRowModalTable(formNaming.NEW_DUPLICATE_COMMUNICATION_FORM)
  );
  dispatch(
    deselectedRowModalTable(formNaming.NEW_DUPLICATE_COMMUNICATION_FORM_OT)
  );
  dispatch(
    deselectedRowModalTable(formNaming.NEW_DUPLICATE_COMMUNICATION_FORM_CAMPAIGN)
  );
  
  dispatch(indexTabFormReset(tabsType.DUPLICATE_COMMUNICATION));

  // Bloque C-6: SAVE_GENERIC_ORDER/SAVE_CAMPAIGN ya populó parent atómicamente.
  // SAVE_COMMUNICATION ya populó la Order Communication child. No hacen falta
  // UPDATE_ORDER + UPDATE_ORDER_RELATIONSHIPS post-create — eliminados.

  // Tarea #8 (2026-04-24): tras crear una OT nueva desde duplicate tab 0,
  // redirigir a la nueva OT (parity con NewComunication/saveData.ts).
  const newParentIdFromSave =
    dataOrderParent?.data?.saveCampaign?.order?.id ??
    dataOrderParent?.data?.saveGenericOrder?.order?.id;
  if (tabSelected === 0 && newParentIdFromSave) {
    const isCampaign = dataForm?.specialTag === OrderType.Campaign;
    return redirect(
      isCampaign
        ? `/work-orders/campaign/${newParentIdFromSave}`
        : `/work-orders/${newParentIdFromSave}`
    );
  }

  return redirect('..');
};
